import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { DEFAULT_GROUP } from './constants';
import { TARIFF_ASSIGNMENTS, UPDATE_TARIFF_CATEGORIZATION } from './graphql';
import {
    assignmentTags,
    countByCommodity,
    matchesCommodities,
    sectionsForGroup,
    tariffsInGroup,
    toggleCommodityTag,
    toPgArrayLiteral,
} from './utils';

const toggleValue = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

export const useTariffLibrary = ({ tariffs, onDelete }) => {
    const navigate = useNavigate();
    const [group, setGroup] = useState(DEFAULT_GROUP);
    const [selectedCommodities, setSelectedCommodities] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const [updateCategorization] = useMutation(UPDATE_TARIFF_CATEGORIZATION, {
        onError: (e) => {
            console.log(e);
            captureException(e);
        },
    });

    // Unnamed tariffs fall back to "Tariff N" by list position (getTariffName). Index the full list
    // so the name doesn't change as filters move the tariff around.
    const nameIndexById = useMemo(
        () => Object.fromEntries((tariffs || []).map((tariff, idx) => [tariff.tariff_id, idx])),
        [tariffs]
    );

    const groupTariffs = useMemo(() => tariffsInGroup(tariffs, group), [tariffs, group]);
    const counts = useMemo(() => countByCommodity(groupTariffs), [groupTariffs]);
    const sections = useMemo(
        () =>
            sectionsForGroup(
                groupTariffs.filter((tariff) => matchesCommodities(tariff.commodity_tags, selectedCommodities)),
                group
            ),
        [groupTariffs, selectedCommodities, group]
    );

    // `fields` is what the cache shows immediately; `update` is what Hasura receives. Apollo rolls
    // the optimistic fields back if the mutation fails.
    const saveCategorization = (tariff, fields, update) =>
        updateCategorization({
            variables: { tariff_id: tariff.tariff_id, update },
            optimisticResponse: {
                tariff: {
                    __typename: 'pricing_tariffs',
                    tariff_id: tariff.tariff_id,
                    category: tariff.category ?? null,
                    commodity_tags: tariff.commodity_tags || [],
                    ...fields,
                },
            },
        });

    return {
        state: { group, counts, sections, selectedCommodities, expandedId, nameIndexById, addOpen, deleteTarget },
        callbacks: {
            setGroup,
            toggleSelectedCommodity: (value) => setSelectedCommodities((prev) => toggleValue(prev, value)),
            toggleExpanded: (tariff_id) => setExpandedId((prev) => (prev === tariff_id ? null : tariff_id)),
            editTariff: (tariff_id) => navigate(`/account/tariff/${tariff_id}`),
            setCategory: (tariff, category) => saveCategorization(tariff, { category }, { category }),
            toggleCommodity: (tariff, value) => {
                const commodity_tags = toggleCommodityTag(tariff.commodity_tags, value);
                saveCategorization(tariff, { commodity_tags }, { commodity_tags: toPgArrayLiteral(commodity_tags) });
            },
            openAdd: () => setAddOpen(true),
            closeAdd: () => setAddOpen(false),
            requestDelete: (tariff) => setDeleteTarget(tariff),
            cancelDelete: () => setDeleteTarget(null),
            confirmDelete: () => {
                onDelete(deleteTarget.tariff_id);
                setDeleteTarget(null);
            },
        },
    };
};

const EMPTY_FORM = { algo_type: '', category: '', commodity_tags: [] };

// The Add Tariff modal's fields, cleared each time it opens.
export const useAddTariffForm = ({ open, onCreate, onClose }) => {
    const [form, setForm] = useState(EMPTY_FORM);

    useEffect(() => {
        if (open) setForm(EMPTY_FORM);
    }, [open]);

    return {
        form,
        canCreate: !!form.algo_type,
        callbacks: {
            setAlgoType: (algo_type) => setForm((prev) => ({ ...prev, algo_type })),
            setCategory: (category) => setForm((prev) => ({ ...prev, category })),
            toggleCommodity: (value) =>
                setForm((prev) => ({ ...prev, commodity_tags: toggleCommodityTag(prev.commodity_tags, value) })),
            create: () => {
                // Insert-ready columns. The page adds ownership (client_id / organization_id) and tariff_type.
                onCreate({
                    algo_type: form.algo_type,
                    category: form.category || null,
                    commodity_tags: toPgArrayLiteral(form.commodity_tags),
                });
                onClose();
            },
        },
    };
};

// Loads when a row opens, and refetches each time it opens again, since overrides are edited on
// other pages. Errors go to Sentry here because Apollo 3.14 deprecates onError on useQuery.
export const useTariffAssignments = (tariff_id) => {
    const { data, loading, error } = useQuery(TARIFF_ASSIGNMENTS, {
        variables: { tariff_id },
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        if (error) captureException(error);
    }, [error]);

    const tags = useMemo(() => assignmentTags(data?.assignments), [data]);

    return { tags, loading: loading && !data, error };
};
